/**
 * HOW THIS KIND OF BUSINESS TALKS.
 *
 * THE RULE THAT DECIDES WHAT BELONGS HERE: this file holds how words are
 * read — the defaults every type starts from, and the sentences built from
 * them — while each type's own words sit in its record in `VERTICALS`. A
 * word changes what is read; it never changes a guard, a query, a seeded row
 * or whether a screen exists.
 *
 * ONE CALL ANSWERS ALL OF IT, so a screen cannot read its title from here and
 * another of its words from somewhere else.
 *
 * WORDS, AND NOTHING ELSE: no suggestion chips for a new person, no pay
 * rhythm for a ficha that does not ask how somebody is paid. A type's
 * suggestion for a field that does not exist is not a suggestion.
 */
import { RECEIVABLE_SOURCE_LABELS } from "./receivable.js";
import { catalogShapeFor } from "./catalog-shape.js";
import { assetCategoriesFor, servicesAssets, verticalOf } from "./verticals.js";
import { takesPlate } from "./asset.js";
import { assignsWork, FACES, offersQuotes } from "./faces.js";
import { ALL_CAPABILITIES, capabilitiesFor, PAIRED_QUESTIONS, settledGrant, } from "./capabilities.js";
import { saleDoor } from "./sale-door.js";
import { MODULES } from "./modules.js";
import { monthName, nextBillingMonth, periodStartOf, todayIn } from "./period.js";
const DEFAULT_WORDS = {
    team: "Empleados",
    person: "empleado",
    parties: "Clientes",
    party: "cliente",
    suppliers: "Proveedores",
    supplier: "proveedor",
    obligations: "Cobros",
    obligation: "cobro",
    obligationFeminine: false,
    catalog: "Productos y servicios",
    catalogItem: "producto o servicio",
    orders: "Pedidos",
    order: "pedido",
    delivered: "Entregado",
    deliverAction: "Entregar",
    deliverActionShort: "Entregar",
    undeliverAction: "Deshacer entrega",
    orderFeminine: false,
    agenda: "Agenda",
    prep: "Preparación",
    prepExit: "Salir de la vista de preparación",
    prepIcon: "list",
};
/**
 * Total over any string: the wizard asks before a type has been picked, and
 * a business whose type this file does not know reads the defaults rather
 * than crashing or showing a blank title.
 */
export function wordsFor(type) {
    return { ...DEFAULT_WORDS, ...verticalOf(type).words };
}
/** The list's filter for the work not delivered yet, in the trade's words. */
export function openFilterLabel(words) {
    return words.open ?? (words.orderFeminine ? "Abiertas" : "Abiertos");
}
/** `null`: this type never asks who receives the service. Total over any string, like `wordsFor`. */
export function recipientFor(businessType) {
    return verticalOf(businessType).recipient ?? null;
}
/** The Extras card's first line for one add-on in this business: `MODULES[id].hint` unless the type has its own. */
export function addOnHint(id, businessType) {
    const pitch = id === "inventario" ? verticalOf(businessType).inventoryPitch : undefined;
    return pitch === undefined ? MODULES[id].hint : `${pitch}: para saber cuánto te queda de cada uno.`;
}
/**
 * THE NAME A CUOTA IS «FOR». The member where the type asks; the client
 * everywhere else, because there the member is a copy of the client's name
 * taken at the alta, and renaming the client never reaches it (`updateParty`
 * is the kernel's and cannot name `app.member`). Resolved where names are
 * read, never synced where they are written.
 */
export function recipientName(businessType, memberName, clientName) {
    return recipientFor(businessType) === null ? clientName : memberName;
}
/**
 * Whether the list of documents holds this business's sales too: «Nueva
 * venta» writes a document here, and a document born delivered is a sale.
 * The same predicate the list's «Tipo: Tickets | Ventas» filter is drawn on
 * -- except where that document is an appointment: the list is the agenda's,
 * and «Citas» says what it holds.
 */
function listHoldsSales(type, modules) {
    const door = saleDoor(type, modules);
    return door?.form === "document" && door.face !== null && FACES[door.face].sideRow !== "appointment";
}
/**
 * THE NAME OF THE LIST ITSELF -- the menu entry, the screen's title and the
 * phone's tab. Where the business sells through its documents and calls them
 * by the generic «Pedidos», the list holds its pedidos AND its sales, and
 * «Ventas» names it. A trade with its own word for its documents names the
 * list by it: «Ventas» in a workshop, «Trabajos», «Casos» or «Proyectos» in a
 * profession (spec 2026-10-21 §20 #9).
 *
 * NOT `orders` itself: that word also names the count («47 pedidos») and the
 * permission sentences, which mean the documents alone.
 */
export function ordersListTitle(type, modules) {
    const { orders } = wordsFor(type);
    return listHoldsSales(type, modules) && orders === DEFAULT_WORDS.orders ? "Ventas" : orders;
}
/**
 * The search box of the document lists (orders and quotes): the backend
 * matches the number, the recipient, the description, the line names and the
 * plate of a document's car. The plate is named only
 * where the trade works on vehicles.
 */
export function documentSearchPlaceholder(type) {
    const plates = servicesAssets(type) && assetCategoriesFor(type).some((category) => takesPlate(category.id));
    return plates ? "Buscar por placa, número, nombre o descripción" : "Buscar por número, nombre o descripción";
}
/** A word that agrees with the document: `agree(words, "anotad")` → «anotado» / «anotada». */
export function agree(words, stem) {
    return `${stem}${words.feminine ? "a" : "o"}`;
}
/** «este» / «esta» in the middle of a sentence; «Este» / «Esta» to start one. */
export function thisOf(words, capital = false) {
    const word = words.feminine ? "esta" : "este";
    return capital ? `${word.charAt(0).toUpperCase()}${word.slice(1)}` : word;
}
/** «El» / «La», to start a sentence. */
export function articleOf(words) {
    return words.feminine ? "La" : "El";
}
/**
 * `face` names ONE document: what its lines are titled. Lists that name a kind
 * in general leave it out.
 */
export function documentWordsFor(kind, businessType, face) {
    const one = kind === "QUOTE" ? "cotización" : wordsFor(businessType).order;
    const feminine = kind === "QUOTE" || wordsFor(businessType).orderFeminine;
    const linesTitle = face !== undefined && FACES[face].linesFromMenu ? "Lo pedido" : kind === "QUOTE" ? "Lo cotizado" : "El trabajo";
    return { one, One: `${one.charAt(0).toUpperCase()}${one.slice(1)}`, feminine, linesTitle };
}
/**
 * The title of the screen that creates one: «Nueva venta» for every document
 * a business sells, «Nueva cotización» for a quote.
 *
 * HERE, AND NOT IN EACH CLIENT, so the phone and the browser cannot title the
 * same form two ways. The quote takes its agreement from its word
 * (`DocumentWords.feminine`).
 */
export function newDocumentTitle(choice, businessType) {
    // The same words as `createLabel("order")`: whoever opens the form reads what they pressed.
    if (choice === "ORDER")
        return "Nueva venta";
    const words = documentWordsFor(choice, businessType);
    return `${words.feminine ? "Nueva" : "Nuevo"} ${words.one}`;
}
/** The button at the foot of that form, in the title's word: «Guardar venta», «Guardar cotización». */
export function newDocumentSaveLabel(choice, businessType) {
    return choice === "ORDER" ? "Guardar venta" : `Guardar ${documentWordsFor(choice, businessType).one}`;
}
/**
 * THE PRODUCT PICKER'S EMPTY LIST, before the first item exists: «Aún no tienes nada en Productos y servicios.», and where typing adds
 * a line (every picker but one that only picks, the restaurant's menu), that
 * a name typed above adds one. For whoever keeps the catalogue the caller adds
 * the button that creates the first item.
 */
export function emptyCatalogText(businessType, typingAdds) {
    const none = `Aún no tienes nada en ${wordsFor(businessType).catalog}.`;
    return typingAdds ? `${none} Escribe su nombre arriba para agregar uno.` : none;
}
/** That button: «Crea tu primer producto o servicio», a salon's «servicio». */
export function firstCatalogAction(businessType) {
    return `Crea tu primer ${wordsFor(businessType).catalogItem}`;
}
/** Configuración's «Garantía por defecto»: where it is copied, in the trade's word and gender. */
export function warrantyDefaultHint(businessType) {
    return `Se copia en cada ${newOrderOf(wordsFor(businessType))}, y ahí la puedes cambiar.`;
}
/** Configuración's «Plazo de pago (días)»: when it starts counting, and where it is copied. */
export function paymentTermHint(businessType) {
    const w = wordsFor(businessType);
    return `Cuántos días tiene el cliente para pagar desde que cierras ${w.orderFeminine ? "la" : "el"} ${w.order}. Se copia en cada ${newOrderOf(w)}.`;
}
/** «venta nueva», «pedido nuevo»: the adjective agrees with the trade's word. */
function newOrderOf(w) {
    return `${w.order} ${w.orderFeminine ? "nueva" : "nuevo"}`;
}
export function obligationMissingMessage(businessType) {
    const words = wordsFor(businessType);
    return `${words.obligationFeminine ? "Esta" : "Este"} ${words.obligation} ya no existe.`;
}
export function cancelObligationTitle(businessType) {
    const words = wordsFor(businessType);
    return `¿Cancelar ${words.obligationFeminine ? "esta" : "este"} ${words.obligation}?`;
}
export function cancelObligationMessage(businessType) {
    const words = wordsFor(businessType);
    return `${words.obligationFeminine ? "Esta" : "Este"} ${words.obligation} desaparecerá de ${words.obligations} y no se puede recuperar.`;
}
export function firstObligationCreatedMessage(businessType, memberName) {
    const words = wordsFor(businessType);
    const ending = words.obligationFeminine ? "a" : "o";
    return `Se agregó a ${memberName} · su ${words.obligationFeminine ? "primera" : "primer"} ${words.obligation} está cread${ending}`;
}
/**
 * WHAT THE ALTA SAYS, the same on both clients: the cobro is created, or --
 * when the plan does not bill this month (a tutoring centre in January) --
 * when the first one comes.
 */
export function enrolmentMessage(businessType, who, plan, obligationCreated) {
    if (obligationCreated || plan === undefined)
        return firstObligationCreatedMessage(businessType, who);
    const month = nextBillingMonth(plan.billingMonths, periodStartOf(todayIn(), plan.frequency));
    return firstObligationLaterMessage(businessType, who, month);
}
/** After an alta in a month the plan does not bill: «… su primer cobro sale en marzo». */
function firstObligationLaterMessage(businessType, memberName, month) {
    const words = wordsFor(businessType);
    return `Se agregó a ${memberName} · su ${words.obligationFeminine ? "primera" : "primer"} ${words.obligation} sale en ${monthName(month)}`;
}
export function obligationUpdatedMessage(businessType) {
    const words = wordsFor(businessType);
    return `${words.obligation.charAt(0).toUpperCase()}${words.obligation.slice(1)} actualizad${words.obligationFeminine ? "a" : "o"}`;
}
export function obligationCancelledMessage(businessType) {
    const words = wordsFor(businessType);
    return `${words.obligation.charAt(0).toUpperCase()}${words.obligation.slice(1)} cancelad${words.obligationFeminine ? "a" : "o"}`;
}
export function noPaymentsForObligationMessage(businessType) {
    const words = wordsFor(businessType);
    return `Todavía no hay pagos contra ${words.obligationFeminine ? "esta" : "este"} ${words.obligation}.`;
}
export function settledObligationMessage(businessType) {
    const words = wordsFor(businessType);
    return `${words.obligationFeminine ? "Esta" : "Este"} ${words.obligation} está pagad${words.obligationFeminine ? "a" : "o"} por completo.`;
}
export function unableToUndoPaymentMessage(businessType) {
    const words = wordsFor(businessType);
    return `No pudimos deshacer. Elimínalo desde ${words.obligationFeminine ? "la" : "el"} ${words.obligation}.`;
}
/**
 * The words of one capability under this business type -- THE ONLY PLACE
 * THEY ARE WRITTEN (spec 2026-09-19-cuotas-permisos-del-taller §1.1). Every screen, list and message reads
 * through here; `Capability` carries no words at all.
 *
 * The common block (question `ledger` and `kernel`) does not vary by type:
 * a church's offerings are income like anybody's. Only the module block reads `wordsFor` -- and
 * `equipo.manage`, which names the people the way the menu does
 * («Empleados», «Colaboradores»).
 */
export function capabilityWords(id, businessType, modules) {
    const w = wordsFor(businessType);
    // Two questions: whether it quotes (the documents it keeps) and whether it hands work to people.
    const quotes = offersQuotes(businessType);
    const assigns = assignsWork(businessType);
    // «los pedidos», «las ventas»: the article agrees with the trade's word.
    const theOrders = `${w.orderFeminine ? "las" : "los"} ${w.orders.toLowerCase()}`;
    const docs = quotes ? `${theOrders} y las cotizaciones` : `${w.orders.toLowerCase()}`;
    const priceScreen = w.catalog;
    const shape = catalogShapeFor(businessType);
    switch (id) {
        case "ledger.record":
            return {
                answer: "Todo: anota ingresos y egresos, y corrige",
                third: "Anota ingresos y egresos, y corrige",
                infinitive: "anotar ingresos y egresos y corregir",
                hint: "Registra lo que entra y lo que sale, corrige cualquier movimiento y contesta qué fue uno.",
            };
        case "ledger.income":
            return {
                answer: "Solo anota ingresos",
                third: "Solo anota ingresos",
                infinitive: "anotar ingresos",
                hint: "Anota lo que entra. No anota egresos ni corrige: eso lo hace quien anota todo.",
            };
        case "ledger.view":
            return {
                answer: "Solo ver los movimientos",
                third: "Ve los movimientos",
                infinitive: "ver los movimientos",
                hint: "Mira el Libro y las deudas. Puede preguntar qué fue un movimiento.",
            };
        case "ledger.analytics":
            return {
                answer: "Ver las gráficas y reportes",
                third: "Ve las gráficas y reportes",
                infinitive: "ver las gráficas y reportes",
                hint: "Abre Analíticas y baja el reporte en PDF. No anota nada.",
            };
        case "cuotas.collect":
            return {
                answer: "Cobra cuotas y lleva clientes",
                third: "Cobra cuotas y lleva clientes",
                infinitive: "cobrar cuotas y llevar clientes",
                hint: "Ve nombres y teléfonos de las familias. Lo que cobre entra al Libro.",
            };
        case "cuotas.plans":
            return {
                answer: "Define los planes y sus precios",
                third: "Define los planes y sus precios",
                infinitive: "definir los planes y sus precios",
                hint: "Crea y cambia los planes que se cobran y lo que cuestan.",
            };
        case "pedidos.handle":
            return quotes
                ? {
                    answer: `Todo: lleva ${docs}`,
                    third: `Lleva ${docs}`,
                    infinitive: `llevar ${docs}`,
                    hint: `Crea cotizaciones y ${w.orders.toLowerCase()}, cambia descripción, líneas y precios, ${assigns ? "asigna trabajadores, " : ""}cobra, cierra y manda el PDF. Lo que cobre entra al Libro.`,
                }
                : {
                    answer: `Todo: toma y entrega ${docs}`,
                    third: `Toma y entrega ${docs}`,
                    infinitive: `tomar y entregar ${docs}`,
                    hint: `Anota ${docs}, cobra abonos y marca entregas. Lo que cobre entra al Libro.`,
                };
        case "pedidos.work":
            return {
                answer: `Solo hace ${theOrders} que le asignen`,
                third: `Hace ${theOrders} que le asignen`,
                infinitive: `hacer ${theOrders} que le asignen`,
                hint: `Ve sólo sus ${w.orders.toLowerCase()}. Sube fotos, anota lo que hizo, cobra y cierra. Lo que cobre entra al Libro. Precios, cambios y correcciones los hace quien lleva ${theOrders}.`,
            };
        case "pedidos.prices":
            // «Solo…» where inventory makes it a question;
            // alone, it is a box and says the whole sentence.
            if (modules.includes("inventario")) {
                const what = shape.words.theList;
                return {
                    answer: `Solo define ${what} y sus precios`,
                    third: `Define ${what} y sus precios`,
                    infinitive: `definir ${what} y sus precios`,
                    hint: `Crea y cambia lo que está en ${priceScreen}.`,
                };
            }
            // A list that asks «¿Qué es?» holds services too, and says so.
            return shape.onlyKind === null
                ? {
                    answer: "Define los productos, los servicios y sus precios",
                    third: "Define los productos, los servicios y sus precios",
                    infinitive: "definir los productos, los servicios y sus precios",
                    hint: `Crea y cambia lo que está en ${priceScreen}.`,
                }
                : {
                    answer: `Define ${shape.words.theList} y sus precios`,
                    third: `Define ${shape.words.theList} y sus precios`,
                    infinitive: `definir ${shape.words.theList} y sus precios`,
                    hint: `Crea y cambia lo que está en el ${priceScreen}.`,
                };
        case "inventario.count":
            return {
                answer: "Solo cuenta las existencias",
                third: "Cuenta las existencias",
                infinitive: "contar las existencias",
                hint: "Anota lo que hay en los conteos abiertos. No ve cuánto debería haber.",
            };
        case "inventario.manage":
            return {
                answer: `Todo: define ${shape.words.theList} y corrige las existencias`,
                third: "Corrige las existencias",
                infinitive: "corregir las existencias",
                hint: `Abre y aplica conteos, anota entradas y salidas, y crea lo que se cuenta en ${priceScreen}.`,
            };
        case "equipo.manage": {
            const team = w.team.toLowerCase();
            // «Solo los maneja» where the payroll makes it a question; alone, it is
            // a box, and a box says the whole sentence.
            return {
                answer: modules.includes("planilla") ? "Solo los maneja" : `Maneja a los ${team}`,
                third: `Maneja a los ${team}`,
                infinitive: `manejar a los ${team}`,
                hint: "Los agrega, cambia sus datos, les da de baja, les da acceso y decide qué hace cada uno. No puede quitarse ni cambiarse a sí mismo.",
            };
        }
        case "planilla.manage":
            return {
                answer: "Todo: los maneja y lleva la planilla",
                third: `Maneja a los ${w.team.toLowerCase()} y lleva la planilla`,
                infinitive: `manejar a los ${w.team.toLowerCase()} y llevar la planilla`,
                hint: "Todo lo anterior, y además cambia lo que gana cada persona, arma, cierra y paga las planillas. Lo que pague entra al Libro.",
            };
        case "planilla.report":
            return {
                answer: "Solo ve los reportes de la planilla",
                third: "Ve los reportes de la planilla",
                infinitive: "ver los reportes de la planilla",
                hint: "El reporte del mes y del año, para las declaraciones. No ve las planillas una por una.",
            };
        case "citas.handle":
            return {
                answer: "Agenda y atiende citas",
                third: "Agenda y atiende citas",
                infinitive: "agendar y atender citas",
                hint: "Ve la agenda, los nombres y teléfonos de los clientes, y cobra lo atendido. Lo que cobre entra al Libro.",
            };
        case "facturacion.issue":
            return {
                answer: "Puede facturar",
                third: "Factura",
                infinitive: "facturar",
                hint: "Emite la factura de una venta y completa el RUC y el corregimiento de un cliente. Ve los nombres, teléfonos y correos de los clientes.",
            };
        case "facturacion.read":
            return {
                answer: "Ve las facturas",
                third: "Ve las facturas",
                infinitive: "ver las facturas",
                hint: `La lista de facturas y notas de crédito, los totales del mes, el PDF y el CSV. No ve ${theOrders} ni los datos de los clientes.`,
            };
    }
}
/** What POST /ledger answers a loose income from somebody who may not write one here: where sales go, and who may. */
export function freeIncomeRefusal(businessType) {
    return `Las ventas se anotan en ${wordsFor(businessType).orders}. Un ingreso suelto lo anota quien también anota egresos.`;
}
/** The heading of each round question. The Libro's never varies; it goes through here so every heading leaves by one door. */
export function questionWords(question, businessType, modules) {
    if (question === "ledger")
        return "¿Qué hace con el Libro?";
    const w = wordsFor(businessType);
    if (question === "catalog")
        return `¿Qué hace con ${catalogShapeFor(businessType).words.theList}?`;
    if (question === "team")
        return `¿Qué hace con los ${w.team.toLowerCase()}?`;
    const orders = w.orders.toLowerCase();
    const article = w.orderFeminine ? "las" : "los";
    // «pedidos y ventas» where the list holds both; a workshop's documents ARE its ventas, said once.
    const both = listHoldsSales(businessType, modules) && ordersListTitle(businessType, modules) !== w.orders;
    return both ? `¿Qué hace con ${article} ${orders} y ventas?` : `¿Qué hace con ${article} ${orders}?`;
}
/**
 * THE WORDS OF A PAIRED QUESTION (`PAIRED_QUESTIONS`): «¿Qué hace con las
 * cuotas?» and its four answers, «Todo» first and «Nada» last, as every round
 * question reads. The two «Solo…» are the boxes' own sentences.
 */
export function pairQuestionWords(module, businessType, modules) {
    const pair = PAIRED_QUESTIONS[module];
    const only = (id) => {
        const w = capabilityWords(id, businessType, modules);
        return { answer: `Solo ${w.answer.charAt(0).toLowerCase()}${w.answer.slice(1)}`, hint: w.hint };
    };
    const all = {
        answer: "Todo: cobra las cuotas y define los planes",
        hint: "Lleva los clientes, cobra, y crea y cambia los planes y lo que cuestan. Lo que cobre entra al Libro.",
    };
    return {
        question: "¿Qué hace con las cuotas?",
        answers: [
            { value: "all", ...all },
            { value: "work", ...only(pair.work) },
            { value: "define", ...only(pair.define) },
            { value: "none", answer: "Nada", hint: null },
        ],
    };
}
/** `capabilitiesFor` with the one condition decided from the type -- what every permissions screen calls. */
export function offeredCapabilities(modules, businessType) {
    return capabilitiesFor(modules, assignsWork(businessType));
}
/**
 * WHAT A PERMISSIONS FORM OPENS WITH: the stored grant settled, minus what the
 * business no longer offers -- a module switched off since. The form cannot
 * draw those boxes, so it must not send them back: the server refuses the
 * whole grant as «no está encendido».
 */
export function grantToEdit(ids, modules, businessType) {
    const offered = new Set(offeredCapabilities(modules, businessType).map((c) => c.id));
    return settledGrant(ids, modules, assignsWork(businessType)).filter((id) => offered.has(id));
}
/**
 * The phrases joined for a person to read. SECOND person is the WhatsApp
 * invitation («para anotar ingresos y egresos y corregir, y llevar las
 * ventas…»); THIRD is the team list («Anota… · Lleva…»). Catalogue order
 * always, so two people with the same permissions read the same.
 */
export function describeCapabilities(ids, person, businessType, modules) {
    const chosen = ALL_CAPABILITIES.filter((c) => ids.includes(c.id)).map((c) => capabilityWords(c.id, businessType, modules));
    if (person === "third")
        return chosen.map((x) => x.third).join(" · ");
    const phrases = chosen.map((x) => x.infinitive);
    if (phrases.length <= 1)
        return phrases.join("");
    return `${phrases.slice(0, -1).join(", ")}, y ${phrases.at(-1)}`;
}
/**
 * The question the ACCESS CARD asks before any of that -- the offer itself,
 * not the form. A QUESTION ABOUT THIS PERSON, not a
 * description of a feature. It names nobody because it is asked on that
 * person's own ficha, with their name at the top of it.
 */
export const ACCESS_QUESTION = "¿Quieres que entre a la app para hacer algo en concreto?";
/**
 * A line of «Te deben», in this business's words: the `pedido` source is the
 * common document, which a clinic calls «Citas» (citas spec §3). The other
 * sources keep `RECEIVABLE_SOURCE_LABELS`.
 */
export function receivableSourceLabel(source, businessType) {
    return source === "pedido" ? wordsFor(businessType).orders : RECEIVABLE_SOURCE_LABELS[source];
}
